import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, Paper, Typography, FormControl, InputLabel, Select, MenuItem, Button, Alert, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, IconButton, Tooltip, TextField, Autocomplete, TableSortLabel, InputAdornment, Chip, TableContainer } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { authService } from '../api/authService';
import { dipendenteService, impostazioniGlobaliService } from '../api/services';
import { controlloOrariService } from '../api/services';
import { BulkAssenzaRequest, BloccoStatusDTO, Dipendente, User } from '../types/types';
import { AccessTime, Search, FilterList, Clear } from '@mui/icons-material';

type Order = 'asc' | 'desc';
type OrderBy = 'negozio' | 'nome' | 'dataControllo' | 'stato';

const ABS_CODES = [
  'FERIE',
  'MALATTIA',
  "MATERNITA'",
  'ROL',
  'LEGGE 104 PERMESSO',
  'CONGEDO MATRIMONIALE'
];

const ControlloOrari: React.FC = () => {
  const user: User | null = useMemo(() => authService.getCurrentUser(), []);
  const isAdmin = user?.ruolo === 'Admin';

  const [dipendenti, setDipendenti] = useState<Dipendente[]>([]);
  const [loadingDip, setLoadingDip] = useState(false);
  const [selectedDip, setSelectedDip] = useState<Dipendente | null>(null);
  const [selectedAbsCode, setSelectedAbsCode] = useState<string>(ABS_CODES[0]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ success: boolean; conflitti: { data: string }[]; inseritiCount: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Blocchi
  const [cutoffDate, setCutoffDate] = useState<Dayjs | null>(dayjs());
  const [blocchi, setBlocchi] = useState<BloccoStatusDTO[]>([]);
  const [loadingBlocchi, setLoadingBlocchi] = useState(false);
  const [dataBloccoAdmin, setDataBloccoAdmin] = useState<Dayjs | null>(dayjs());
  const [savingBloccoAdmin, setSavingBloccoAdmin] = useState(false);
  const [dataBloccoCoordinatori, setDataBloccoCoordinatori] = useState<Dayjs | null>(dayjs());
  const [savingBloccoCoordinatori, setSavingBloccoCoordinatori] = useState(false);

  // Blocco globale (solo per visualizzazione stato)
  const [dataBloccoGlobale, setDataBloccoGlobale] = useState<Dayjs | null>(null);
  const [dataBloccoCoordinatoriSalvata, setDataBloccoCoordinatoriSalvata] = useState<Dayjs | null>(null);

  // Ordinamento tabella
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('negozio');

  // Filtri tabella
  const [filterNegozio, setFilterNegozio] = useState('');
  const [filterNome, setFilterNome] = useState('');
  const [filterDataControllo, setFilterDataControllo] = useState('');
  const [filterStato, setFilterStato] = useState<'' | 'OK' | 'KO'>('');

  // Carica data blocco globale
  useEffect(() => {
    const loadGlobalBlock = async () => {
      if (!isAdmin) return;
      try {
        const [data, dataCoordinatori] = await Promise.all([
          impostazioniGlobaliService.getDataBloccoGlobale(),
          impostazioniGlobaliService.getDataBloccoCoordinatori()
        ]);
        setDataBloccoGlobale(data ? dayjs(data) : null);
        setDataBloccoCoordinatoriSalvata(dataCoordinatori ? dayjs(dataCoordinatori) : null);
      } catch (e) {
        console.error('Errore caricamento data blocco globale:', e);
      }
    };
    loadGlobalBlock();
  }, [isAdmin]);

  useEffect(() => {
    const loadDip = async () => {
      if (!isAdmin) return;
      setLoadingDip(true);
      try {
        const all = await dipendenteService.getAll();
        // Ordinati già per negozio/nome dal backend? Backup sort local.
        all.sort((a,b) => a.neg.localeCompare(b.neg) || a.nome.localeCompare(b.nome));
        setDipendenti(all);
      } catch (e) {
        setError('Errore caricamento dipendenti');
      } finally {
        setLoadingDip(false);
      }
    };
    loadDip();
  }, [isAdmin]);

  const doBulkInsert = async (override: boolean) => {
    if (!selectedDip || !startDate || !endDate || !selectedAbsCode) {
      setError('Compila tutti i campi');
      return;
    }
    setError(null);
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const dip = selectedDip;
      if (!dip) throw new Error('Dipendente non trovato');
      const payload: BulkAssenzaRequest = {
        codicePersonale: dip.codicePersonale,
        nomeDipendente: dip.nome,
        dataInizio: startDate.format('YYYY-MM-DD'),
        dataFine: endDate.format('YYYY-MM-DD'),
        codiceTipoOrario: selectedAbsCode,
        override
      };
      const res = await controlloOrariService.bulkAssenze(payload);
      setBulkResult(res);
      if (!res.success && res.conflitti.length > 0 && !override) {
        // mostra popup browser semplice
        const giorni = res.conflitti.map(c => c.data).join(', ');
        const conferma = window.confirm(`Conflitti su giorni: ${giorni}\nVuoi continuare e sostituire i turni esistenti?`);
        if (conferma) {
          // retry con override true
          await doBulkInsert(true);
        }
      } else if (res.success) {
        alert(`Inserite ${res.inseritiCount} assenze per ${selectedDip?.nome}`);
      }
    } catch (e: any) {
      setError(e.message || 'Errore inserimento assenze');
    } finally {
      setBulkLoading(false);
    }
  };

  const loadBlocchiForDate = useCallback(async (dateStr: string) => {
    setLoadingBlocchi(true);
    setError(null);
    try {
      const res = await controlloOrariService.getBlocchi(dateStr);
      // Ordinamento negozio + nome già lato backend, backup sort
      res.sort((a,b) => a.negozio.localeCompare(b.negozio) || a.nome.localeCompare(b.nome));
      setBlocchi(res);
    } catch (e: any) {
      console.error('Errore caricamento blocchi:', e);
      setError('Errore caricamento blocchi: ' + (e?.response?.data?.error || e?.message || 'Errore sconosciuto'));
    } finally {
      setLoadingBlocchi(false);
    }
  }, []);

  const loadBlocchi = useCallback(() => {
    if (!cutoffDate?.isValid()) return;
    return loadBlocchiForDate(cutoffDate.format('YYYY-MM-DD'));
  }, [cutoffDate, loadBlocchiForDate]);

  useEffect(() => {
    if (isAdmin && cutoffDate?.isValid()) {
      loadBlocchiForDate(cutoffDate.format('YYYY-MM-DD'));
    }
    // Solo caricamento iniziale: non ricaricare a ogni battitura nel DatePicker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // Gestione ordinamento
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Funzione di comparazione per ordinamento
  const getComparator = (order: Order, orderBy: OrderBy) => {
    return (a: BloccoStatusDTO, b: BloccoStatusDTO) => {
      let aVal: string | boolean;
      let bVal: string | boolean;
      
      switch (orderBy) {
        case 'negozio':
          aVal = a.negozio;
          bVal = b.negozio;
          break;
        case 'nome':
          aVal = a.nome;
          bVal = b.nome;
          break;
        case 'dataControllo':
          aVal = a.dataControllo || '';
          bVal = b.dataControllo || '';
          break;
        case 'stato':
          aVal = a.ok ? 'OK' : 'KO';
          bVal = b.ok ? 'OK' : 'KO';
          break;
        default:
          return 0;
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal);
        return order === 'asc' ? cmp : -cmp;
      }
      return 0;
    };
  };

  // Filtro e ordinamento blocchi
  const filteredAndSortedBlocchi = useMemo(() => {
    let result = [...blocchi];
    
    // Applica filtri
    if (filterNegozio) {
      result = result.filter(b => b.negozio.toLowerCase().includes(filterNegozio.toLowerCase()));
    }
    if (filterNome) {
      result = result.filter(b => b.nome.toLowerCase().includes(filterNome.toLowerCase()));
    }
    if (filterDataControllo) {
      result = result.filter(b => (b.dataControllo || '').toLowerCase().includes(filterDataControllo.toLowerCase()));
    }
    if (filterStato) {
      result = result.filter(b => (b.ok ? 'OK' : 'KO') === filterStato);
    }
    
    // Applica ordinamento
    result.sort(getComparator(order, orderBy));
    
    return result;
  }, [blocchi, filterNegozio, filterNome, filterDataControllo, filterStato, order, orderBy]);

  if (!isAdmin) {
    return <Container sx={{ mt:4 }}><Alert severity="error">Accesso riservato agli Admin</Alert></Container>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 6 }}>
      <Typography variant="h4" gutterBottom sx={{ ml: { xs: 10, sm: 14 } }}>Controllo Orari</Typography>

      {/* Sezione Bulk Assenze */}
      <Paper sx={{ p:2, mb:4 }} elevation={3}>
        <Typography variant="h6" gutterBottom>Inserimento Massivo Assenze</Typography>
        <Box sx={{ display:'flex', flexWrap:'wrap', gap:2, alignItems: 'center' }}>
          <Autocomplete
            size="small"
            sx={{ 
              minWidth: 320,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2', borderWidth: 2 },
              }
            }}
            options={dipendenti}
            loading={loadingDip}
            value={selectedDip}
            onChange={(_, newValue) => setSelectedDip(newValue)}
            getOptionLabel={(option) => `${option.neg} - ${option.nome}`}
            filterOptions={(options, { inputValue }) => {
              const searchTerm = inputValue.toLowerCase();
              return options.filter(option => 
                option.nome.toLowerCase().includes(searchTerm) ||
                option.neg.toLowerCase().includes(searchTerm)
              );
            }}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Dipendente" 
                placeholder="Cerca per nome o negozio..."
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <InputAdornment position="start">
                        <Search sx={{ color: 'text.disabled', ml: 0.5 }} />
                      </InputAdornment>
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {loadingDip ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.codicePersonale}>
                <Box sx={{ py: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{option.nome}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    📍 {option.neg}
                  </Typography>
                </Box>
              </li>
            )}
            isOptionEqualToValue={(option, value) => option.codicePersonale === value.codicePersonale}
            noOptionsText="Nessun dipendente trovato"
            loadingText="Caricamento..."
          />
          <FormControl size="small" sx={{ minWidth:200 }}>
            <InputLabel>Tipo Assenza</InputLabel>
            <Select value={selectedAbsCode} label="Tipo Assenza" onChange={e => setSelectedAbsCode(e.target.value)}>
              {ABS_CODES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <DatePicker label="Data Inizio" value={startDate} onChange={val => setStartDate(val)} slotProps={{ textField:{ size:'small'} }} />
            <DatePicker 
              label="Data Fine" 
              value={endDate} 
              onChange={val => setEndDate(val)} 
              slotProps={{ textField:{ size:'small'} }}
              referenceDate={startDate || undefined}
              minDate={startDate || undefined}
            />
          </LocalizationProvider>
          <Button variant="contained" disabled={bulkLoading} onClick={() => doBulkInsert(false)}>Inserisci</Button>
          {bulkLoading && <CircularProgress size={24} />}
        </Box>
        {bulkResult && !bulkResult.success && bulkResult.conflitti.length > 0 && (
          <Alert severity="warning" sx={{ mt:2 }}>Conflitti trovati: {bulkResult.conflitti.map(c => c.data).join(', ')}</Alert>
        )}
      </Paper>

      {/* Sezione Blocchi */}
      <Paper sx={{ p:2 }} elevation={3}>
        <Typography variant="h6" gutterBottom>Verifica Blocchi</Typography>
        
        {/* Indicatore blocco globale attivo */}
        {dataBloccoGlobale && (
          <Alert severity="error" sx={{ mb:2 }}>
            <strong>🔒 Blocco Attivo:</strong> Le modifiche degli utenti diversi da Coordinatore/Admin per date ≤ {dataBloccoGlobale.format('DD/MM/YYYY')} sono bloccate.
          </Alert>
        )}
        {dataBloccoCoordinatoriSalvata && (
          <Alert severity="error" sx={{ mb:2 }}>
            <strong>🔒 Blocco Coordinatori Attivo:</strong> Le modifiche degli utenti Coordinatore per date ≤ {dataBloccoCoordinatoriSalvata.format('DD/MM/YYYY')} sono bloccate.
          </Alert>
        )}
        
        <Box sx={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:2, mb:2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <DatePicker
              label="Controlla Blocchi sino al"
              value={cutoffDate}
              onChange={val => setCutoffDate(val)}
              onAccept={val => {
                if (val?.isValid()) {
                  loadBlocchiForDate(val.format('YYYY-MM-DD'));
                }
              }}
              slotProps={{ textField:{ size:'small'} }}
            />
          </LocalizationProvider>
          <Button variant="outlined" onClick={() => loadBlocchi()} disabled={loadingBlocchi}>Aggiorna</Button>
          {loadingBlocchi && <CircularProgress size={22} />}
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:2, mb:2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <DatePicker label="Data blocco" value={dataBloccoAdmin} onChange={val => setDataBloccoAdmin(val)} slotProps={{ textField:{ size:'small'} }} />
          </LocalizationProvider>
          <Button
            variant="contained"
            color="error"
            disabled={savingBloccoAdmin || !dataBloccoAdmin}
            onClick={async () => {
              if (!dataBloccoAdmin) return;
              setSavingBloccoAdmin(true);
              setError(null);
              try {
                const dataStr = dataBloccoAdmin.format('YYYY-MM-DD');
                
                // Imposta SOLO il blocco globale (tutte le operazioni sui turni vengono bloccate centralmente)
                await impostazioniGlobaliService.setDataBloccoGlobale(dataStr);
                
                // Ricarica la data dal backend per essere sicuri che sia stata salvata correttamente
                const dataSalvata = await impostazioniGlobaliService.getDataBloccoGlobale();
                if (dataSalvata) {
                  setDataBloccoGlobale(dayjs(dataSalvata));
                } else {
                  setDataBloccoGlobale(null);
                }
                
                // Aggiorna la cutoffDate alla data del blocco e ricarica la tabella blocchi
                // così mostra subito le persone con turni non consolidati fino a quella data
                const bloccoDay = dayjs(dataStr);
                setCutoffDate(bloccoDay);
                // Ricarica diretta con la nuova data: il DatePicker non scatena più fetch a ogni battitura.
                setLoadingBlocchi(true);
                try {
                  const res = await controlloOrariService.getBlocchi(dataStr);
                  res.sort((a, b) => a.negozio.localeCompare(b.negozio) || a.nome.localeCompare(b.nome));
                  setBlocchi(res);
                } catch (e: any) {
                  console.error('Errore caricamento blocchi dopo blocco totale:', e);
                } finally {
                  setLoadingBlocchi(false);
                }
                
                alert(`Data blocco impostata al ${dataStr}.\n\nLe operazioni sui turni degli utenti diversi da Coordinatore/Admin sono bloccate per date ≤ ${dataStr}.`);
              } catch (e) {
                setError('Errore nell\'impostazione data blocco');
              } finally {
                setSavingBloccoAdmin(false);
              }
            }}
          >
            Imposta data blocco
          </Button>
          <Button
            variant="outlined"
            color="success"
            disabled={savingBloccoAdmin || !dataBloccoGlobale}
            onClick={async () => {
              const conferma = window.confirm('Sei sicuro di voler RIMUOVERE il blocco?\n\nTutti gli utenti torneranno a poter modificare, salvo altri blocchi.');
              if (!conferma) return;
              
              setSavingBloccoAdmin(true);
              setError(null);
              try {
                // Rimuovi il blocco globale impostando null
                await impostazioniGlobaliService.setDataBloccoGlobale(null);
                
                // Ricarica la data dal backend
                const dataSalvata = await impostazioniGlobaliService.getDataBloccoGlobale();
                setDataBloccoGlobale(dataSalvata ? dayjs(dataSalvata) : null);
                
                alert('Blocco rimosso con successo.');
              } catch (e) {
                setError('Errore nella rimozione del blocco globale');
              } finally {
                setSavingBloccoAdmin(false);
              }
            }}
          >
            Rimuovi blocco globale
          </Button>
          {savingBloccoAdmin && <CircularProgress size={22} />}
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', flexWrap:'wrap', gap:2, mb:2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
            <DatePicker label="Data blocco coordinatori" value={dataBloccoCoordinatori} onChange={val => setDataBloccoCoordinatori(val)} slotProps={{ textField:{ size:'small'} }} />
          </LocalizationProvider>
          <Button
            variant="contained"
            color="error"
            disabled={savingBloccoCoordinatori || !dataBloccoCoordinatori}
            onClick={async () => {
              if (!dataBloccoCoordinatori) return;
              setSavingBloccoCoordinatori(true);
              setError(null);
              try {
                const dataStr = dataBloccoCoordinatori.format('YYYY-MM-DD');
                await impostazioniGlobaliService.setDataBloccoCoordinatori(dataStr);
                setDataBloccoCoordinatoriSalvata(dayjs(dataStr));
                alert(`Data blocco coordinatori impostata al ${dataStr}.`);
              } catch (e) {
                setError('Errore nell\'impostazione data blocco coordinatori');
              } finally {
                setSavingBloccoCoordinatori(false);
              }
            }}
          >
            Imposta data blocco coordinatori
          </Button>
          <Button
            variant="outlined"
            color="success"
            disabled={savingBloccoCoordinatori || !dataBloccoCoordinatoriSalvata}
            onClick={async () => {
              if (!window.confirm('Sei sicuro di voler RIMUOVERE il blocco coordinatori?')) return;
              setSavingBloccoCoordinatori(true);
              setError(null);
              try {
                await impostazioniGlobaliService.setDataBloccoCoordinatori(null);
                setDataBloccoCoordinatoriSalvata(null);
                alert('Blocco coordinatori rimosso con successo.');
              } catch (e) {
                setError('Errore nella rimozione del blocco coordinatori');
              } finally {
                setSavingBloccoCoordinatori(false);
              }
            }}
          >
            Rimuovi blocco coordinatori
          </Button>
          {savingBloccoCoordinatori && <CircularProgress size={22} />}
        </Box>

        {/* Barra filtri moderna */}
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1.5, 
          mb: 2, 
          p: 2, 
          backgroundColor: '#f8f9fa', 
          borderRadius: 2,
          alignItems: 'center'
        }}>
          <FilterList sx={{ color: 'text.secondary', mr: 0.5 }} />
          <TextField
            size="small"
            placeholder="Negozio"
            value={filterNegozio}
            onChange={(e) => setFilterNegozio(e.target.value)}
            sx={{ 
              width: 150,
              backgroundColor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#e0e0e0' },
                '&:hover fieldset': { borderColor: '#1976d2' },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            placeholder="Nome"
            value={filterNome}
            onChange={(e) => setFilterNome(e.target.value)}
            sx={{ 
              width: 150,
              backgroundColor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#e0e0e0' },
                '&:hover fieldset': { borderColor: '#1976d2' },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            placeholder="Data Controllo"
            value={filterDataControllo}
            onChange={(e) => setFilterDataControllo(e.target.value)}
            sx={{ 
              width: 150,
              backgroundColor: 'white',
              borderRadius: 1,
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#e0e0e0' },
                '&:hover fieldset': { borderColor: '#1976d2' },
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={filterStato}
              onChange={(e) => setFilterStato(e.target.value as '' | 'OK' | 'KO')}
              displayEmpty
              sx={{ 
                backgroundColor: 'white',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e0e0e0' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#1976d2' },
              }}
            >
              <MenuItem value="">Stato: Tutti</MenuItem>
              <MenuItem value="OK">
                <Chip label="OK" size="small" color="success" sx={{ height: 20 }} />
              </MenuItem>
              <MenuItem value="KO">
                <Chip label="KO" size="small" color="error" sx={{ height: 20 }} />
              </MenuItem>
            </Select>
          </FormControl>
          {(filterNegozio || filterNome || filterDataControllo || filterStato) && (
            <Button
              size="small"
              variant="text"
              startIcon={<Clear />}
              onClick={() => {
                setFilterNegozio('');
                setFilterNome('');
                setFilterDataControllo('');
                setFilterStato('');
              }}
              sx={{ ml: 'auto', color: 'text.secondary' }}
            >
              Pulisci filtri
            </Button>
          )}
        </Box>

        {/* Contatore risultati */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {filteredAndSortedBlocchi.length} di {blocchi.length} risultati
          {(filterNegozio || filterNome || filterDataControllo || filterStato) && ' (filtrati)'}
        </Typography>

        <TableContainer sx={{ maxHeight: 500, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                <TableSortLabel
                  active={orderBy === 'negozio'}
                  direction={orderBy === 'negozio' ? order : 'asc'}
                  onClick={() => handleRequestSort('negozio')}
                >
                  Negozio
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                <TableSortLabel
                  active={orderBy === 'nome'}
                  direction={orderBy === 'nome' ? order : 'asc'}
                  onClick={() => handleRequestSort('nome')}
                >
                  Nome
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                <TableSortLabel
                  active={orderBy === 'dataControllo'}
                  direction={orderBy === 'dataControllo' ? order : 'asc'}
                  onClick={() => handleRequestSort('dataControllo')}
                >
                  Data Controllo
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                <TableSortLabel
                  active={orderBy === 'stato'}
                  direction={orderBy === 'stato' ? order : 'asc'}
                  onClick={() => handleRequestSort('stato')}
                >
                  Stato
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ backgroundColor: '#fafafa', fontWeight: 600 }}>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedBlocchi.map(b => {
              const isOk = b.ok;
              return (
                <TableRow 
                  key={b.codicePersonale} 
                  sx={{ 
                    backgroundColor: isOk ? '#e8f5e9' : '#ffebee',
                    '&:hover': { 
                      backgroundColor: isOk ? '#c8e6c9' : '#ffcdd2',
                      cursor: 'pointer'
                    },
                    transition: 'background-color 0.2s'
                  }}
                >
                  <TableCell>{b.negozio}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{b.nome}</TableCell>
                  <TableCell>{b.dataControllo || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={isOk ? 'OK' : 'KO'} 
                      size="small" 
                      color={isOk ? 'success' : 'error'}
                      sx={{ fontWeight: 600, minWidth: 45 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Vai a Orario Dipendente">
                      <IconButton 
                        size="small" 
                        onClick={() => {
                          const mese = dayjs().month() + 1;
                          const anno = dayjs().year();
                          window.location.href = `/orario-dipendente?neg=${encodeURIComponent(b.negozio)}&dip=${encodeURIComponent(b.nome)}&mese=${mese}&anno=${anno}`;
                        }}
                        sx={{ 
                          color: 'primary.main',
                          '&:hover': { backgroundColor: 'primary.light', color: 'white' }
                        }}
                      >
                        <AccessTime fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredAndSortedBlocchi.length === 0 && !loadingBlocchi && (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                  {blocchi.length === 0 ? 'Nessun dato disponibile' : 'Nessun risultato con i filtri applicati'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </Paper>
      {error && <Alert severity="error" sx={{ mt:3 }}>{error}</Alert>}
    </Container>
  );
};

export default ControlloOrari;
